/**
 * 참여자/마스터 관련 유틸리티
 *
 * masterSlice.ts, mainUserSlice.ts에서 추출한 헬퍼 함수.
 */
import type { MasterUser } from '@/types/store';

/**
 * 사용자 ID 정규화. 앞뒤 공백 제거 + 소문자화.
 * portal_id가 API 응답마다 공백/대소문자가 달라지는 경우를 흡수한다.
 */
export const normalizeUserId = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase();

/**
 * 사용자 ID 동일 비교. 빈 값끼리는 항상 false(빈 값 == 빈 값으로 오탐 방지).
 */
export const isSameUserId = (a: unknown, b: unknown): boolean => {
  const left = normalizeUserId(a);
  return !!left && left === normalizeUserId(b);
};

/**
 * 주어진 사용자가 마스터인지 확인하는 헬퍼.
 * 원본: masterUsers.find(m => m.user === (externalUserId || wehagoId))
 * 비교는 정규화된 ID 기준(공백/대소문자 차이로 인한 오판 방지).
 */
export const isMasterUser = (masterUsers: MasterUser[], userId: string): boolean =>
  !!userId && masterUsers.some((m) => isSameUserId(m.user, userId));

/** 핀 키에서 순수 userId 추출 (compositeKey 'abc:screen' → 'abc') */
export const bareUserId = (pinKey: string): string =>
  pinKey.endsWith(':screen') ? pinKey.slice(0, -7) : pinKey;
